// Утилиты для обработки ошибок API
import type { 
  ApiResponse, 
  ApiErrorType, 
  ExtendedApiError, 
  ValidationError 
} from '../../types/common/base.types';
import { mapErrorCodeToMessage } from '../../utils/errorMessages';
import { normalizeNumericValue } from '../../utils/numberNormalization';
import { getStoredImpersonation, clearStoredImpersonation } from '../../features/impersonation/impersonationStorage';

/**
 * Тикет эмуляции отозван/истёк на сервере (актор потерял право, сессия
 * закрыта и т.п.) — запрос ушёл от имени админа, а не цели, хотя фронт
 * думает, что эмуляция активна. Чистим локальное состояние и перезагружаем,
 * чтобы не показать админу данные/UI, отрисованные под чужой личностью.
 */
function handleImpersonationRevoked(code: string | undefined): void {
  if (code !== 'IMPERSONATION_FORBIDDEN') return;
  if (!getStoredImpersonation()) return;
  clearStoredImpersonation();
  window.location.reload();
}

// ============================================================================
// СОЗДАНИЕ API ОШИБОК
// ============================================================================

/**
 * Создает расширенную ошибку API с дополнительной информацией
 */
export function createExtendedApiError(
  message: string,
  type: ApiErrorType,
  options: {
    status?: number;
    validationErrors?: ValidationError[];
    operationId?: string;
    endpoint?: string;
    method?: string;
    canRetry?: boolean;
    userMessage?: string;
    code?: string;
    details?: unknown;
  } = {}
): ExtendedApiError {
  const error = new Error(message) as ExtendedApiError;
  
  error.type = type;
  error.status = options.status;
  error.validationErrors = options.validationErrors;
  error.operationId = options.operationId;
  error.endpoint = options.endpoint;
  error.method = options.method;
  error.timestamp = Date.now();
  error.canRetry = options.canRetry ?? determineCanRetry(type, options.status);
  error.code = options.code;
  error.details = options.details;
  
  // Приоритет: явный userMessage > маппинг кода > генерация по типу
  error.userMessage = options.userMessage 
    || mapErrorCodeToMessage(options.code, undefined) 
    || generateUserMessage(type, options.status);
  error.name = 'ExtendedApiError';
  
  return error;
}

/**
 * Определяет, можно ли повторить запрос для данного типа ошибки
 */
function determineCanRetry(type: ApiErrorType, status?: number): boolean {
  switch (type) {
    case 'network':
      return true;
    case 'server':
      return status !== 503; // Service Unavailable обычно временная
    case 'unauthorized':
    case 'forbidden':
    case 'not_found':
    case 'validation':
    case 'conflict':
      return false;
    case 'client':
      return status ? status >= 429 : false; // Rate limiting
    default:
      return false;
  }
}

/**
 * Генерирует понятное сообщение для пользователя
 */
function generateUserMessage(type: ApiErrorType, status?: number): string {
  switch (type) {
    case 'network':
      return 'Проблемы с подключением к серверу. Проверьте интернет-соединение.';
    case 'server':
      return 'Временные проблемы на сервере. Попробуйте позже.';
    case 'unauthorized':
      return 'Требуется авторизация. Войдите в систему заново.';
    case 'forbidden':
      return 'У вас нет прав доступа к этому ресурсу.';
    case 'not_found':
      return 'Запрашиваемые данные не найдены.';
    case 'validation':
      return 'Некорректные данные. Проверьте введенную информацию.';
    case 'conflict':
      return 'Данные были изменены другим пользователем. Обновите страницу.';
    case 'client':
      if (status === 429) {
        return 'Слишком много запросов. Попробуйте через несколько секунд.';
      }
      return 'Ошибка в запросе. Обратитесь к администратору.';
    default:
      return 'Произошла неизвестная ошибка. Обратитесь к администратору.';
  }
}

// ============================================================================
// НОРМАЛИЗАЦИЯ ДАННЫХ
// ============================================================================

const IDENTIFIER_FIELD_PATTERN =
  /(^|[_\s-])(id|ид)($|[_\s-])|(?:id|ид)$|code|код|sku|артикул|barcode|штрих|version|верси|revision|ревизи|etag|номер|phone|телефон|инн|inn|кпп|kpp|снилс|snils/i;

const MEASURABLE_FIELD_PATTERN =
  /amount|price|cost|revenue|plan|fact|qty|quantity|count|percent|rate|volume|sum|total|value|weight|coefficient|limit|balance|year|month|day|week|цена|стоим|выруч|план|факт|колич|об[ъь]ем|сумм|итог|процент|ставк|доля|вес|коэфф|лимит|остат|год|месяц|день|недел|руб|ед[._\s]|кнп/i;

const MONTH_VALUE_FIELD_PATTERN = /^(?:m|м)\d{1,2}$/i;

function isMeasurableNumericField(key: string): boolean {
  if (IDENTIFIER_FIELD_PATTERN.test(key)) return false;
  return MEASURABLE_FIELD_PATTERN.test(key) || MONTH_VALUE_FIELD_PATTERN.test(key);
}

function normalizeMeasurableNumericString(key: string, value: string): string | number {
  if (!isMeasurableNumericField(key)) return value;

  const trimmed = value.trim();
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(trimmed)) return value;

  // Leading zeroes are significant for codes accidentally returned in a
  // measure-shaped field (for example `accountNumber` or imported Excel data).
  if (/^[+-]?0\d/.test(trimmed)) return value;

  const numericValue = Number(trimmed);
  if (!Number.isFinite(numericValue)) return value;

  // JSON numbers cannot safely represent larger integer identifiers/amounts.
  if (Number.isInteger(numericValue) && !Number.isSafeInteger(numericValue)) return value;

  return normalizeNumericValue(trimmed) ?? value;
}

/**
 * Рекурсивно нормализует только измеримые числовые поля API. Идентификаторы,
 * коды, версии и неизвестные строковые контракты остаются строками.
 */
export function normalizeResponseData<T>(data: T): T {
  // Если null или undefined - возвращаем как есть
  if (data === null || data === undefined) {
    return data;
  }

  // Если это массив - обрабатываем каждый элемент
  if (Array.isArray(data)) {
    return data.map(item => normalizeResponseData(item)) as T;
  }

  // Если это объект - обрабатываем каждое поле
  if (typeof data === 'object') {
    const normalized: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        normalized[key] = normalizeMeasurableNumericString(key, value);
      } else if (typeof value === 'object' && value !== null) {
        normalized[key] = normalizeResponseData(value);
      } else {
        normalized[key] = value;
      }
    }
    
    return normalized as T;
  }

  // Для примитивов возвращаем как есть
  return data;
}

// ============================================================================
// ОБРАБОТКА ОТВЕТОВ API
// ============================================================================

/**
 * Обрабатывает ответ от fetch и преобразует в типизированный результат или ошибку
 */
export async function handleApiResponse<T>(
  response: Response,
  endpoint?: string,
  method?: string
): Promise<T> {
  const responseText = await response.text();
  let responseData: unknown;

  // Парсим JSON если возможно
  try {
    responseData = responseText ? JSON.parse(responseText) : {};
  } catch {
    responseData = { error: responseText };
  }

  // Если ответ успешный
  if (response.ok) {
    // Проверяем, является ли ответ ApiResponse
    if (isApiResponse(responseData)) {
      const apiResponse = responseData as ApiResponse<T>;
      
      if (!apiResponse.success) {
        const responseAny = apiResponse as unknown as Record<string, unknown>;
        const code = typeof responseAny.code === 'string' ? responseAny.code : undefined;
        const correlationId = typeof responseAny.correlationId === 'string' ? responseAny.correlationId : undefined;
        
        const err = createExtendedApiError(
          apiResponse.error || 'API вернул ошибку',
          'server',
          {
            status: response.status,
            operationId: apiResponse.operationId,
            endpoint,
            method,
            validationErrors: extractValidationErrors(apiResponse.data),
            userMessage: typeof responseAny.userMessage === 'string' ? responseAny.userMessage : (apiResponse.message || apiResponse.error),
            code,
            details: responseAny.details,
          }
        );
        
        if (correlationId) {
          err.correlationId = correlationId;
        }

        handleImpersonationRevoked(code);
        throw err;
      }

      // Нормализуем данные перед возвратом (конвертируем числовые строки в числа)
      return normalizeResponseData(apiResponse.data!);
    }
    
    // Нормализуем данные перед возвратом
    return normalizeResponseData(responseData as T);
  }

  // Обрабатываем ошибочный ответ
  const errorType = determineErrorType(response.status);
  const validationErrors = extractValidationErrors(responseData);
  
  const errorMessage = extractErrorMessage(responseData, responseText);
  const operationId = extractOperationId(responseData);
  const correlationId = extractCorrelationId(responseData);
  
  const responseAny = responseData as Record<string, unknown>;
  const code = typeof responseAny?.code === 'string' ? responseAny.code : undefined;

  const err = createExtendedApiError(
    errorMessage,
    errorType,
    {
      status: response.status,
      validationErrors,
      operationId,
      endpoint,
      method,
      code,
      details: responseAny?.details,
    }
  );
  
  if (correlationId) {
    err.correlationId = correlationId;
  }

  handleImpersonationRevoked(code);
  throw err;
}

/**
 * Проверяет, является ли объект ApiResponse
 */
function isApiResponse(data: unknown): data is ApiResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'success' in data &&
    typeof (data as { success: unknown }).success === 'boolean'
  );
}

/**
 * Определяет тип ошибки по статус коду
 */
function determineErrorType(status: number): ApiErrorType {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 400) return 'validation';
  if (status >= 400 && status < 500) return 'client';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * Извлекает сообщение об ошибке из ответа
 */
function extractErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    
    if (typeof obj.error === 'string') return obj.error;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.details === 'string') return obj.details;
  }
  
  return fallback || 'Неизвестная ошибка';
}

/**
 * Извлекает operationId из ответа
 */
function extractOperationId(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    return typeof obj.operationId === 'string' ? obj.operationId : undefined;
  }
  return undefined;
}

function extractCorrelationId(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    return typeof obj.correlationId === 'string' ? obj.correlationId : undefined;
  }
  return undefined;
}

/**
 * Извлекает ошибки валидации из ответа
 */
function extractValidationErrors(data: unknown): ValidationError[] | undefined {
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    
    // Проверяем структуру {validationErrors: []}
    if (Array.isArray(obj.validationErrors)) {
      return obj.validationErrors.filter((item): item is ValidationError =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as ValidationError).field === 'string' &&
        typeof (item as ValidationError).message === 'string'
      );
    }
  }
  return undefined;
}

// ============================================================================
// ОБРАБОТКА СЕТЕВЫХ ОШИБОК
// ============================================================================

/**
 * Обрабатывает ошибки fetch (сетевые ошибки, таймауты)
 */
export function handleNetworkError(
  error: unknown,
  endpoint?: string,
  method?: string
): ExtendedApiError {
  let message = 'Ошибка сети';
  let canRetry = true;
  
  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      message = 'Запрос был отменен';
      canRetry = false;
    } else if (error.name === 'TimeoutError') {
      message = 'Таймаут запроса';
      canRetry = true;
    } else if (error.message.includes('fetch')) {
      message = 'Ошибка сетевого соединения';
      canRetry = true;
    } else {
      message = error.message;
    }
  }
  
  return createExtendedApiError(
    message,
    'network',
    {
      endpoint,
      method,
      canRetry,
      userMessage: canRetry 
        ? 'Проблемы с подключением. Попробуйте еще раз.'
        : 'Запрос был отменен.'
    }
  );
}

// ============================================================================
// УТИЛИТЫ ДЛЯ КОМПОНЕНТОВ
// ============================================================================

/**
 * Извлекает данные для отображения ошибки пользователю
 */
export interface ErrorDisplayInfo {
  title: string;
  message: string;
  details?: string;
  canRetry: boolean;
  canReport: boolean;
  severity: 'error' | 'warning' | 'info';
  operationId?: string;
  timestamp: number;
}

/**
 * Подготавливает информацию об ошибке для отображения
 */
export function prepareErrorForDisplay(error: unknown): ErrorDisplayInfo {
  // Если это наша расширенная ошибка API
  if (isExtendedApiError(error)) {
    return {
      title: getErrorTitle(error.type),
      message: error.userMessage,
      details: error.message !== error.userMessage ? error.message : undefined,
      canRetry: error.canRetry,
      canReport: shouldAllowReporting(error.type),
      severity: getErrorSeverity(error.type),
      operationId: error.operationId,
      timestamp: error.timestamp
    };
  }
  
  // Обычная ошибка JavaScript
  if (error instanceof Error) {
    return {
      title: 'Произошла ошибка',
      message: 'Что-то пошло не так. Попробуйте обновить страницу.',
      details: error.message,
      canRetry: true,
      canReport: true,
      severity: 'error',
      timestamp: Date.now()
    };
  }
  
  // Неизвестный тип ошибки
  return {
    title: 'Неизвестная ошибка',
    message: 'Произошла неизвестная ошибка. Обратитесь к администратору.',
    details: typeof error === 'string' ? error : JSON.stringify(error),
    canRetry: false,
    canReport: true,
    severity: 'error',
    timestamp: Date.now()
  };
}

/**
 * Проверяет, является ли ошибка ExtendedApiError
 */
function isExtendedApiError(error: unknown): error is ExtendedApiError {
  return error instanceof Error && error.name === 'ExtendedApiError';
}

/**
 * Возвращает заголовок для типа ошибки
 */
function getErrorTitle(type: ApiErrorType): string {
  switch (type) {
    case 'network': return 'Проблемы с подключением';
    case 'server': return 'Ошибка сервера';
    case 'unauthorized': return 'Требуется авторизация';
    case 'forbidden': return 'Доступ запрещен';
    case 'not_found': return 'Данные не найдены';
    case 'validation': return 'Некорректные данные';
    case 'conflict': return 'Конфликт данных';
    case 'client': return 'Ошибка запроса';
    default: return 'Произошла ошибка';
  }
}

/**
 * Определяет серьезность ошибки
 */
function getErrorSeverity(type: ApiErrorType): 'error' | 'warning' | 'info' {
  switch (type) {
    case 'validation':
    case 'conflict':
      return 'warning';
    case 'not_found':
      return 'info';
    default:
      return 'error';
  }
}

/**
 * Определяет, можно ли отправить отчет об ошибке
 */
function shouldAllowReporting(type: ApiErrorType): boolean {
  // Не отправляем отчеты о валидации и ошибках авторизации
  return !['validation', 'unauthorized', 'forbidden'].includes(type);
}

// ============================================================================
// УТИЛИТА ДЛЯ УЛУЧШЕННОГО FETCH
// ============================================================================

/**
 * Задержка с использованием Promise
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Расширенная версия fetch с автоматической обработкой ошибок и retry логикой
 * 
 * Автоматически повторяет запросы при:
 * - 401 (Unauthorized) - может быть временная проблема с токеном
 * - 429 (Rate Limit) - ждет и повторяет
 * - 5xx (Server errors) - временные проблемы сервера
 * - Сетевые ошибки (network errors)
 * 
 * Использует экспоненциальную задержку: 1s, 2s, 4s
 */
export async function fetchWithErrorHandling<T>(
  url: string,
  options: RequestInit & { timeout?: number; maxRetries?: number } = {}
): Promise<T> {
  const { timeout = 10000, maxRetries: requestedAttempts = 3, signal: externalSignal, ...fetchOptions } = options;
  // maxRetries — это общее число попыток, а не число повторов. Первая попытка выполняется
  // всегда: иначе maxRetries: 0 («без повторов») не отправлял запрос вовсе и падал
  // с «Все попытки запроса исчерпаны».
  const maxRetries = Math.max(1, requestedAttempts);
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Если внешний signal уже отменён — не начинаем попытку
    if (externalSignal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Связываем внешний signal (от React Query при смене query key) с inner controller,
    // чтобы внутренний fetch был отменён сразу при отмене React Query.
    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal
      });

      // Проверяем, нужно ли повторить запрос
      if (shouldRetry(response.status, attempt, maxRetries)) {
        const retryAfterHeader = response.headers.get('Retry-After');
        const retryDelay = getRetryDelay(attempt, response.status, retryAfterHeader);

        if (import.meta.env.DEV) {
          console.warn(
            `⚠️ API Retry: ${response.status} на ${url}, попытка ${attempt + 1}/${maxRetries}, ждем ${retryDelay}ms`
          );
        }

        await delay(retryDelay);
        continue; // Повторяем запрос
      }

      return await handleApiResponse<T>(
        response,
        url,
        fetchOptions.method || 'GET'
      );
    } catch (error) {
      // Если это наша ExtendedApiError, проверяем canRetry
      if (error instanceof Error && error.name === 'ExtendedApiError') {
        const apiError = error as ExtendedApiError;
        
        // Если можно повторить и еще есть попытки
        if (apiError.canRetry && attempt < maxRetries - 1) {
          const retryDelay = getRetryDelay(attempt, apiError.status);
          
          if (import.meta.env.DEV) {
            console.warn(
              `⚠️ API Retry: ${apiError.type} на ${url}, попытка ${attempt + 1}/${maxRetries}, ждем ${retryDelay}ms`
            );
          }
          
          lastError = error;
          await delay(retryDelay);
          continue; // Повторяем запрос
        }
        
        throw error;
      }
      
      // Сетевая ошибка
      const networkError = handleNetworkError(error, url, fetchOptions.method || 'GET');
      
      // Повторяем при сетевых ошибках (кроме AbortError)
      if (networkError.canRetry && attempt < maxRetries - 1) {
        const retryDelay = getRetryDelay(attempt);
        
        if (import.meta.env.DEV) {
          console.warn(
            `⚠️ API Retry: Сетевая ошибка на ${url}, попытка ${attempt + 1}/${maxRetries}, ждем ${retryDelay}ms`
          );
        }
        
        lastError = networkError;
        await delay(retryDelay);
        continue; // Повторяем запрос
      }
      
      throw networkError;
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }
  }

  // Если дошли сюда, значит все попытки исчерпаны
  throw lastError || new Error('Все попытки запроса исчерпаны');
}

/**
 * Определяет, нужно ли повторить запрос на основе статус кода
 * - 429: максимум 1 retry (сервер rate-limited, бить повторно бессмысленно)
 * - 5xx: до maxRetries (сервер может восстановиться)
 */
function shouldRetry(status: number, attempt: number, maxRetries: number): boolean {
  if (attempt >= maxRetries - 1) return false;

  // 429: только 1 retry (attempt 0 → retry 1 раз)
  if (status === 429) return attempt < 1;

  // 5xx: стандартное количество retry
  if (status >= 500 && status < 600) return true;

  return false;
}

/**
 * Вычисляет задержку перед повторной попыткой
 * - 429: использует Retry-After header от сервера + jitter
 * - 5xx: exponential backoff + jitter
 * Jitter предотвращает thundering herd (одновременный retry всех клиентов)
 */
function getRetryDelay(attempt: number, status?: number, retryAfterHeader?: string | null): number {
  const jitter = Math.random() * 1000; // 0-1000ms случайный разброс

  // Для rate limit: используем Retry-After header если есть
  if (status === 429) {
    if (retryAfterHeader) {
      const retryAfterSeconds = parseInt(retryAfterHeader, 10);
      if (!isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
        // Ограничиваем до 60 секунд максимум
        return Math.min(retryAfterSeconds * 1000, 60000) + jitter;
      }
    }
    // Fallback: 5 секунд + jitter
    return 5000 + jitter;
  }

  // Для серверных ошибок: exponential backoff + jitter
  return Math.min(1000 * Math.pow(2, attempt), 4000) + jitter;
}
